import React, { useCallback, useMemo, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import DoorBackOutlinedIcon from '@mui/icons-material/DoorBackOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useTranslation } from 'react-i18next';

import { formatSpotRegion } from '@shared/utils/assistantScript';
import AssistantScriptsService from '@shared/services/AssistantScriptsService';

import AiInspectorHeader from '../AiScripts/AiInspectorHeader';
import AssistantTester from '../AiScripts/AssistantTester';
import KnowledgeTab from '../AiScripts/KnowledgeTab';
import LiveBrandingStrip from '../AiScripts/LiveBrandingStrip';
import ScriptTab from '../AiScripts/ScriptTab';
import type { AiEntity } from '../AiScripts/types';
import { useAiInspectorEntity } from '../AiScripts/useAiInspectorEntity';
import { useAiInspectorStyles } from '../AiScripts/inspectorStyles';
import { useEditorMode } from '../EditorModeContext';
import { useDrift } from '../DriftContext';
import { useEditorAiData } from '../AiScripts/EditorAiDataContext';

interface IAIScriptInspectorProps {
  trigger: string;
  scriptId: string | null;
}

type Tab = 'script' | 'knowledge' | 'test';

const TABS: { id: Tab; labelKey: string; labelFallback: string }[] = [
  { id: 'script', labelKey: 'editorMode.aiTabScript', labelFallback: 'Script' },
  {
    id: 'knowledge',
    labelKey: 'editorMode.aiTabKnowledge',
    labelFallback: 'Knowledge',
  },
  { id: 'test', labelKey: 'editorMode.aiTabTest', labelFallback: 'Test' },
];

const scriptDriftStyles = {
  amber: {
    display: 'flex',
    gap: '9px',
    padding: '10px 12px',
    borderRadius: '11px',
    backgroundColor: 'rgba(245, 166, 35, 0.14)',
    color: '#f6d49a',
    marginBottom: '12px',
  },
  red: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '11px',
    backgroundColor: 'rgba(255, 77, 77, 0.14)',
    color: '#ffb0b0',
    marginBottom: '12px',
  },
  removeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    borderRadius: '10px',
    padding: '9px 12px',
    fontSize: '12.5px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    color: '#ff8585',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
  },
};

const AIScriptInspector = ({ trigger, scriptId }: IAIScriptInspectorProps) => {
  const { t: translate } = useTranslation();
  const classes = useAiInspectorStyles();
  const { clearSelection, aiTreeSection } = useEditorMode();
  const { triggerDrift } = useDrift();
  const { refetchAll } = useEditorAiData();

  const {
    entity,
    ragRefetch,
    mapName,
    brandingId,
    isLiveBranding,
    createCategory,
    createPrimary,
    createTriggers,
  } = useAiInspectorEntity({ trigger, scriptId });

  const [tab, setTab] = useState<Tab>('script');

  const handleClose = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  // When no script + no RAG items exist yet for this trigger (or both exist
  // but in a different branding/map scope), we still want the inspector to
  // surface Create script / Add knowledge — never "Entity unavailable", which
  // is a dead end the user can't recover from. The synthetic entity inherits
  // the active mode's category so the header chip reads correctly (TRIGGER for
  // an AI trigger being created, SPOT for a spot).
  const effectiveEntity = useMemo<AiEntity>(() => {
    const kindFor = createCategory === 'spot' ? 'ui' : 'area';
    if (entity) {
      // A trigger/spot can have knowledge but no script yet — aggregateEntities
      // surfaces that as a knowledge-only (ctx) entity. When the user opened it
      // from the Scripts section (not browsing Knowledge), present it as a
      // trigger/spot so the Script / Knowledge / Test tabs render — with
      // "Create script" and the attached knowledge — instead of the
      // knowledge-only layout. The category keeps the header chip + create
      // trigger type correct (region → area_entry, spot → ui_click).
      if (entity.kind === 'ctx' && aiTreeSection !== 'knowledge') {
        return {
          ...entity,
          kind: kindFor,
          category: createCategory,
          primaryTrigger: entity.primaryTrigger || createPrimary,
        };
      }
      return entity;
    }
    return {
      id: `synthetic:${trigger}`,
      kind: kindFor,
      name: formatSpotRegion(createPrimary) || createPrimary || trigger,
      primaryTrigger: createPrimary || trigger,
      aliases: [],
      script: null,
      contextItems: [],
      scriptState: 'none',
      contextState: 'none',
      overallState: 'empty',
      category: createCategory,
    };
  }, [entity, trigger, aiTreeSection, createCategory, createPrimary]);

  // The Test tab probes how Noa answers against the configured knowledge — it
  // is meaningless without any, so gate it on the entity having context items.
  const hasKnowledge = effectiveEntity.contextItems.length > 0;
  // Defensive: if the user is on the Test tab and then removes the last
  // knowledge item, fall back to the Script tab instead of showing an
  // orphaned tester.
  const activeTab: Tab = tab === 'test' && !hasKnowledge ? 'script' : tab;

  const driftScriptId = effectiveEntity.script?.scriptId ?? null;
  const isOrphanedScript = useMemo(
    () =>
      !!driftScriptId &&
      triggerDrift.rows.some(
        (row) => row.status === 'orphaned' && row.scriptId === driftScriptId,
      ),
    [driftScriptId, triggerDrift],
  );
  const isNewTrigger = useMemo(
    () =>
      !effectiveEntity.script &&
      triggerDrift.rows.some(
        (row) =>
          row.status === 'new' &&
          row.trigger.toLowerCase() ===
            (effectiveEntity.primaryTrigger || trigger).toLowerCase(),
      ),
    [effectiveEntity, trigger, triggerDrift],
  );

  const handleRemoveScript = useCallback(async () => {
    if (!driftScriptId) {
      return;
    }
    try {
      await AssistantScriptsService.deleteAssistantScript(driftScriptId);
      await refetchAll();
      clearSelection();
    } catch (removeError) {
      console.error(
        `Failed to remove assistant script: ${(removeError as Error).message}`,
        { scriptId: driftScriptId },
      );
    }
  }, [driftScriptId, refetchAll, clearSelection]);

  const renderDriftBanner = () => {
    if (isNewTrigger) {
      return (
        <Box sx={scriptDriftStyles.amber}>
          <AutorenewOutlinedIcon
            sx={{ fontSize: 16, flexShrink: 0, mt: '1px' }}
          />
          <Typography sx={{ fontSize: '12px', lineHeight: 1.45 }}>
            {translate('editorMode.driftNewTriggerBanner')}
          </Typography>
        </Box>
      );
    }
    if (isOrphanedScript) {
      return (
        <Box sx={scriptDriftStyles.red}>
          <Box sx={{ display: 'flex', gap: '9px' }}>
            <ErrorOutlineOutlinedIcon
              sx={{ fontSize: 16, flexShrink: 0, mt: '1px' }}
            />
            <Typography sx={{ fontSize: '12px', lineHeight: 1.45 }}>
              {translate('editorMode.driftOrphanScriptBanner')}
            </Typography>
          </Box>
          <Box
            component="button"
            type="button"
            sx={scriptDriftStyles.removeButton}
            onClick={handleRemoveScript}
            data-testid="ai-inspector-remove-orphaned-script-btn"
          >
            <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
            {translate('editorMode.driftRemoveScript')}
          </Box>
        </Box>
      );
    }
    return null;
  };

  // Knowledge-only entities skip the tab strip; the spec stacks regions card +
  // knowledge card + tester directly because there is nothing to put under the
  // Script tab.
  if (effectiveEntity.kind === 'ctx') {
    return (
      <Box sx={classes.panel}>
        <AiInspectorHeader entity={effectiveEntity} onClose={handleClose} />
        <Box sx={classes.body}>
          {isLiveBranding ? <LiveBrandingStrip /> : null}
          {renderDriftBanner()}
          <Box sx={classes.card}>
            <Box sx={classes.cardHeader}>
              <Typography component="span" sx={classes.cardHeaderTitle}>
                <DoorBackOutlinedIcon sx={{ fontSize: 13 }} />
                {translate('editorMode.aiAvailableInRegions')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {effectiveEntity.contextItems
                .flatMap((item) => item.tags)
                .filter((tag, index, arr) => arr.indexOf(tag) === index)
                .map((tag) => (
                  <Box key={tag} component="span" sx={classes.regionChip}>
                    <DoorBackOutlinedIcon />
                    {tag}
                  </Box>
                ))}
            </Box>
          </Box>
          <KnowledgeTab
            entity={effectiveEntity}
            brandingId={brandingId}
            mapName={mapName}
            onChanged={ragRefetch}
          />
          <Box sx={classes.card}>
            <Box sx={classes.cardHeader}>
              <Typography component="span" sx={classes.cardHeaderTitle}>
                {translate('editorMode.aiTabTest', 'Test')}
              </Typography>
            </Box>
            <AssistantTester
              entity={effectiveEntity}
              brandingId={brandingId}
              mapName={mapName}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={classes.panel}>
      <AiInspectorHeader entity={effectiveEntity} onClose={handleClose} />
      <Box sx={classes.body}>
        {isLiveBranding ? <LiveBrandingStrip /> : null}
        {renderDriftBanner()}
        <Box sx={classes.tabs} role="tablist">
          {TABS.map((definition) => {
            const disabled = definition.id === 'test' && !hasKnowledge;
            const tabButton = (
              <Box
                component="button"
                type="button"
                role="tab"
                aria-selected={activeTab === definition.id}
                aria-disabled={disabled}
                disabled={disabled}
                sx={[
                  classes.tab,
                  activeTab === definition.id && classes.tabActive,
                  disabled && classes.tabDisabled,
                ]}
                onClick={() => {
                  if (disabled) {
                    return;
                  }
                  setTab(definition.id);
                }}
                data-testid={`ai-inspector-tab-${definition.id}`}
              >
                {translate(definition.labelKey, definition.labelFallback)}
              </Box>
            );
            if (disabled) {
              return (
                <Tooltip
                  key={definition.id}
                  title={translate('editorMode.aiTestNoKnowledge')}
                >
                  <Box component="span" sx={{ flex: 1, display: 'flex' }}>
                    {tabButton}
                  </Box>
                </Tooltip>
              );
            }
            return (
              <React.Fragment key={definition.id}>{tabButton}</React.Fragment>
            );
          })}
        </Box>

        {activeTab === 'script' ? (
          <ScriptTab
            entity={effectiveEntity}
            trigger={effectiveEntity.primaryTrigger || trigger}
            createTriggers={createTriggers}
            mapName={mapName}
            brandingId={brandingId}
            onScriptChanged={ragRefetch}
          />
        ) : null}
        {activeTab === 'knowledge' ? (
          <KnowledgeTab
            entity={effectiveEntity}
            brandingId={brandingId}
            mapName={mapName}
            onChanged={ragRefetch}
          />
        ) : null}
        {activeTab === 'test' ? (
          <Box sx={classes.card}>
            <AssistantTester
              entity={effectiveEntity}
              brandingId={brandingId}
              mapName={mapName}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default AIScriptInspector;
